/**
 * Service Worker Controller
 *
 * Registers the application service worker for offline caching capabilities.
 * This controller is loaded on every page via the V4 application layout.
 *
 * The service worker (app/javascript/service-worker.js) uses Workbox to implement:
 * - NetworkFirst strategy for HTML documents (5 min cache)
 * - CacheFirst strategy for CSS/JS (30 day cache)
 * - CacheFirst strategy for images (30 day cache)
 *
 * When an update is detected (updatefound event), pre-caches:
 * - Internal navigation links (documents cache)
 * - Stylesheets and scripts (assets cache)
 *
 * The worker claims its clients on activation (see service-worker.js), so Turbo Drive
 * stays enabled everywhere — including the page that performed the registration.
 *
 * @see https://developer.mozilla.org/en-US/docs/Web/API/ServiceWorkerRegistration/updatefound_event
 * @extends ApplicationController
 */
import ApplicationController from './application_controller'

export default class extends ApplicationController {
  connect() {
    if ('serviceWorker' in navigator) {
      if (!navigator.serviceWorker.controller) {
        // Register the service worker, and wait for it to become active
        let self = this

        navigator.serviceWorker.register('/service-worker.js', { scope: '/' }).then(
          function (registration) {
            self.addBreadcrumb('ServiceWorker', 'registration_success', {
              scope: registration.scope,
            })

            registration.addEventListener('updatefound', function () {
              self.addBreadcrumb('ServiceWorker', 'update_found', {
                state: registration.installing?.state,
              })

              caches.open('documents').then(function (cache) {
                let links = document.querySelectorAll('a[href^="/"]:not([rel="nofollow"])')
                let urls = Array.from(links).map((elem) => elem.getAttribute('href'))
                urls = urls.concat(document.location.pathname)
                cache.addAll(Array.from(new Set(urls)))
              })

              caches.open('assets-styles-and-scripts').then(function (cache) {
                let queryLinks = (attribute, selector) => {
                  let links = document.querySelectorAll(selector)
                  let urls = Array.from(links).map((elem) => elem.getAttribute(attribute))
                  return Array.from(new Set(urls))
                }

                cache.addAll(queryLinks('href', 'link[rel="stylesheet"][href^="/"]'))
                cache.addAll(queryLinks('src', 'script[src^="/"]'))
              })
            })
          },
          function (err) {
            // Restricted browsers reject SW registration with a detail-free
            // Error("Rejected") — noise behind AppSignal incident #23. Report the rest.
            if (err?.message === 'Rejected') {
              self.addBreadcrumb('ServiceWorker', 'registration_failed')
            } else {
              self.reportError(err, { action: 'service_worker_registration' })
            }
          },
        )
      }
    }
  }
}
